import { Image } from 'expo-image';
import { Link } from 'expo-router';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { cities } from '@/lib/events-data';
import type { Locale } from '@todo/core/i18n/locales';

type CityPagePathname = '/[lang]/[city]/events' | '/[lang]/[city]/tours';

/**
 * The /events and /tours hubs' city chips — real links to each city's own
 * /[city]/events or /[city]/tours page (not an in-page filter), so the hub is
 * the crawl entry point to every city's full list, like the /boats hub's chips
 * are for /boat-rental/[city]. Pass only cities whose page was exported
 * (useCityListingPages).
 */
export function HubCityChips({
  cityIds,
  pathname,
  locale,
}: {
  cityIds: string[];
  pathname: CityPagePathname;
  locale: Locale;
}) {
  const theme = useTheme();
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.chipsRow}>
      {cityIds.map((id) => {
        const city = cities.find((c) => c.id === id);
        if (!city) return null;
        return (
          <Link key={city.id} href={{ pathname, params: { lang: locale, city: city.id } }} asChild>
            <Pressable accessibilityRole="link">
              {({ pressed }) => (
                // Pill styling on an inner View: <Link asChild> drops a
                // Pressable's function-style `style` on web.
                <View
                  style={[
                    styles.chip,
                    { backgroundColor: theme.card, borderColor: theme.border },
                    pressed && { opacity: 0.85 },
                  ]}>
                  <Image source={city.image} style={styles.cityImg} contentFit="cover" />
                  <Text style={[styles.chipText, { color: theme.text }]}>{city.name}</Text>
                </View>
              )}
            </Pressable>
          </Link>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  chipsRow: {
    gap: Spacing.one + 2,
    paddingRight: Spacing.three,
    paddingVertical: 2,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingLeft: 6,
    paddingRight: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
  },
  cityImg: {
    width: 22,
    height: 22,
    borderRadius: 11,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
  },
});
